export const DEFAULT_PRECISION_MONEY = 8

/**
 *
 * @param {number} value
 * @param {number} precision
 * @returns {number} Returns precision value for specified decimal places
 */
export const getPrecision = (value, precision = DEFAULT_PRECISION_MONEY) => {
  let strValue = String(value)
  if (strValue.includes('e') || strValue.includes('E')) {
    if (value >= 1e21) return value
    strValue = value.toFixed(precision + 20)
  }
  const [int, dec = ""] = strValue.split(".")
  return Number(
    int + "." + dec.slice(0, precision).padEnd(precision, "0")
  )
}

export const plus = (a, b) => {
  const A = normalize(a)
  const B = normalize(b)

  const scale = Math.max(A.scale, B.scale)

  const aInt = toScale(A.int, A.scale, scale) * BigInt(A.sign)
  const bInt = toScale(B.int, B.scale, scale) * BigInt(B.sign)

  return formatResult(aInt + bInt, scale)
}

export const minus = (a, b) => {
  return plus(a, '-' + b)
}

export const times = (a, b) => {
  const A = normalize(a)
  const B = normalize(b)

  const result =
    (A.int * BigInt(A.sign)) *
    (B.int * BigInt(B.sign))

  return formatResult(result, A.scale + B.scale)
}

export const divide = (a, b, precision = 18) => {
  const A = normalize(a)
  const B = normalize(b)

  if (B.int === 0n) {
    throw new Error('Division by zero')
  }

  const numerator =
    (A.int * (10n ** BigInt(precision))) *
    BigInt(A.sign)

  const denominator =
    B.int * BigInt(B.sign)

  const result = numerator / denominator

  const scale =
    precision + A.scale - B.scale

  return formatResult(result, scale)
}

function expandScientificNotation (numStr) {
  const match = numStr.match(/^(\d+(?:\.\d+)?)[eE]([+-]?\d+)$/)
  if (!match) {
    throw new Error(`Invalid number: ${numStr}`)
  }

  const [, mantissa, expStr] = match
  const exponent = parseInt(expStr, 10)
  const [intPart, fracPart = ''] = mantissa.split('.')
  const digits = intPart + fracPart
  const decimalPos = intPart.length + exponent

  if (decimalPos <= 0) {
    return '0.' + '0'.repeat(-decimalPos) + digits.replace(/^0+/, '') || '0'
  }

  if (decimalPos >= digits.length) {
    return digits + '0'.repeat(decimalPos - digits.length)
  }

  return digits.slice(0, decimalPos) + '.' + digits.slice(decimalPos)
}

function normalize (numStr) {
  numStr = String(numStr).trim()

  let sign = 1

  if (numStr.startsWith('-')) {
    sign = -1
    numStr = numStr.slice(1)
  } else if (numStr.startsWith('+')) {
    numStr = numStr.slice(1)
  }

  if (/[eE]/.test(numStr)) {
    numStr = expandScientificNotation(numStr)
  }

  if (!numStr.includes('.')) {
    return {
      sign,
      int: BigInt(numStr),
      scale: 0
    }
  }

  const [whole, frac] = numStr.split('.')

  return {
    sign,
    int: BigInt((whole || '0') + frac),
    scale: frac.length
  }
}

function toScale (intVal, fromScale, toScale) {
  if (fromScale === toScale) return intVal

  return intVal * (10n ** BigInt(toScale - fromScale))
}

function formatResult (intVal, scale) {
  let sign = ''

  if (intVal < 0n) {
    sign = '-'
    intVal = -intVal
  }

  let str = intVal.toString()

  if (scale === 0) {
    return sign + str
  }

  while (str.length <= scale) {
    str = '0' + str
  }

  const whole = str.slice(0, -scale) || '0'
  let frac = str.slice(-scale)

  frac = frac.replace(/0+$/, '')

  if (!frac) {
    return sign + whole
  }

  return sign + whole + '.' + frac
}
